/*
  Copyright (c) 2025 Fluxonix Inc. All rights reserved.
*/

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  Container,
  Snackbar,
  Alert,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { api } from '../api';
import { useAuth } from "../auth/AuthProvider";

function OtpLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const prefilledEmail = location?.state?.email || '';
  const returnTo = location?.state?.returnTo || '';

  const [email, setEmail] = useState(prefilledEmail);
  const [otpSent, setOtpSent] = useState(!!prefilledEmail); // Skip to OTP if email is prefilled
  const [otp, setOtp] = useState('');
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();

  useEffect(() => {
    const token = localStorage.getItem("lpo_token");
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const now = Math.floor(Date.now() / 1000);
        if (payload.exp && payload.exp > now) {
          navigate("/lpo-entry-mode");
        } else {
          localStorage.removeItem("lpo_token");
        }
      } catch (err) {
        localStorage.removeItem("lpo_token");
      }
    }
  }, [navigate]);

  useEffect(() => {
    if (prefilledEmail) {
      // Auto-trigger OTP when redirected with email
      sendOtp(prefilledEmail);
    }
  }, [prefilledEmail]);

  const sendOtp = async (overrideEmail) => {
    const targetEmail = overrideEmail || email;
    if (!targetEmail) return;

    setLoading(true);
    setError('');
    try {
      await api.post("/auth/send-otp", { email: targetEmail });
      setOtpSent(true);
      setInfo('OTP sent to your email.');
    } catch (err) {
      setError('Failed to send OTP. Please check the email and try again.');
    } finally {
      setLoading(false);
    }
  };

  const verifyOtp = async () => {
  setLoading(true);
  try {
    const response = await api.post("/auth/verify-otp", {
      email,
      otp,
    });

    const { access_token } = response.data;

    const userData = response.data?.user;
    console.log(userData.tenant_slug)
    
    let decoded = {};
    try {
      decoded = jwtDecode(access_token);
    } catch (e) {
      console.error("❌ Failed to decode JWT in login:", e);
    }
    // 🔑 Single source of truth: backend-declared type, with token fallback
    const isLpo = userData.user_type === "lpo" || !!decoded.lpo_id;

      // 🎯 Derive roles
      let derivedRoles = [];
      if (userData.role === "b2c_user") {
        derivedRoles = ["admin", "field_agent"];
      } else if (Array.isArray(userData.role)) {
        derivedRoles = userData.role;
      } else if (userData.role) {
        derivedRoles = [userData.role];
      }

      
      const normalized = {
    ...userData,
    lpo_id: userData.lpo_id ?? decoded.lpo_id ?? null,
    user_id: decoded.user_id ?? null,       // for non-LPO flows if you add it later
    userType: isLpo ? "lpo" : "user",
    identityKey: isLpo ? "lpo_id" : "user_id",
    identityValue: isLpo ? decoded.lpo_id : decoded.user_id,
    roles: derivedRoles,
    permissions: Array.isArray(userData.permissions)
      ? userData.permissions
      : userData.permissions
      ? [userData.permissions]
      : [],
  };

    if (email.endsWith("@mailer.banyannation.com")) {
      // ✅ Persist everything
      localStorage.setItem("fluxonix_user", JSON.stringify(normalized));
      //localStorage.setItem("fluxonix_token", access_token);
      localStorage.setItem("lpo_token", access_token);
      login(normalized, access_token)
      navigate("/lpo-entry-mode");
    } else {
      localStorage.setItem("fluxonix_user", JSON.stringify(normalized));
      localStorage.setItem("fluxonix_token", access_token);
      //localStorage.setItem("b2c_token", access_token);  
      login(normalized, access_token)
      navigate(returnTo || "/secure/voice", { replace: true });
    }
    } catch (err) {
      setError("Invalid OTP or expired. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm" sx={{ mt: 6 }}>
      <Typography variant="h5" gutterBottom>
        Email Login
      </Typography>

      {!otpSent ? (
        <>
          <TextField
            label="Email"
            type="email"
            fullWidth
            margin="normal"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          <Button
            variant="contained"
            fullWidth
            disabled={!email || loading}
            onClick={() => sendOtp()}
          >
            {loading ? 'Sending OTP...' : 'Send OTP'}
          </Button>
        </>
      ) : (
        <>
          <Typography sx={{ mt: 2 }}>OTP sent to: {email}</Typography>
          <TextField
            label="Enter OTP"
            fullWidth
            margin="normal"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
          />
          <Button
            variant="contained"
            fullWidth
            disabled={!otp || loading}
            onClick={verifyOtp}
          >
            {loading ? 'Verifying...' : 'Verify OTP'}
          </Button>
          <Button
            variant="text"
            fullWidth
            disabled={loading}
            onClick={() => sendOtp()}
            sx={{ mt: 1 }}
          >
            Resend code
          </Button>
        </>
      )}

      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
        <Alert severity="error" onClose={() => setError('')}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar open={!!info} autoHideDuration={6000} onClose={() => setInfo('')}>
        <Alert severity="info" onClose={() => setInfo('')}>
          {info}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default OtpLogin;
